import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Phone, MapPin, Clock, Search, Filter, Play, Eye, Calendar, Settings, Users, Award, TrendingUp, ChevronRight, Globe, Shield, Truck, CheckCircle, Wrench, Gauge, PoundSterling, ChevronDown, MousePointer } from 'lucide-react';
import { useTrucks } from '../hooks/useTrucks';
import { useAllContent, useCustomStyles } from '../hooks/useContent';
import { LoadingCard } from '../components/LoadingSpinner';
import NewsletterSignup from '../components/NewsletterSignup';
import SEO from '../components/SEO';
import { SoldRibbon } from '../components/SoldRibbon';
import RecentlySoldCarousel from '../components/RecentlySoldCarousel';

const HomePage = () => {
  const { trucks: featuredTrucks, loading, error } = useTrucks({ featured: true, limit: 6 });
  const { trucks: latestTrucks, loading: latestLoading } = useTrucks({ limit: 8 });
  const { trucks: hireVehicles, loading: hireLoading, error: hireError } = useTrucks({ available_for_hire: true, limit: 6 });
  const { trucks: allTrucks } = useTrucks({ limit: 100 }); // For search results count
  const { contentData, loading: contentLoading } = useAllContent();
  
  const [searchFilters, setSearchFilters] = useState({
    make: '',
    model: '',
    bodytype: '',
    priceRange: '',
    mileage: '',
    availability: ''
  });

  // Calculate real-time search results count
  const searchResultsCount = useMemo(() => {
    if (!allTrucks || allTrucks.length === 0) return 0;
    
    let filtered = allTrucks.filter(truck => truck.make === 'MAN'); // Only MAN trucks
    
    if (searchFilters.availability) {
      if (searchFilters.availability === 'for-sale') {
        // Show vehicles that are not exclusively for hire (can be both for sale and hire)
        filtered = filtered.filter(truck => !truck.available_for_hire || truck.price);
      } else if (searchFilters.availability === 'for-hire') {
        // Show only vehicles available for hire
        filtered = filtered.filter(truck => truck.available_for_hire === true);
      }
    }
    
    if (searchFilters.bodytype) {
      filtered = filtered.filter(truck => 
        truck.bodytype && truck.bodytype.toLowerCase().includes(searchFilters.bodytype.toLowerCase())
      );
    }
    
    if (searchFilters.mileage) {
      const [min, max] = searchFilters.mileage.includes('+') 
        ? [parseInt(searchFilters.mileage.replace('+', '')), Infinity]
        : searchFilters.mileage.split('-').map(n => parseInt(n));
      
      filtered = filtered.filter(truck => {
        const mileage = truck.mileage || 0;
        return mileage >= min && (max === Infinity || mileage <= max);
      });
    }
    
    if (searchFilters.priceRange) {
      const [min, max] = searchFilters.priceRange.includes('+')
        ? [parseInt(searchFilters.priceRange.replace('+', '')), Infinity]
        : searchFilters.priceRange.split('-').map(n => parseInt(n));
      
      filtered = filtered.filter(truck => {
        const price = truck.price || 0;
        return price >= min && (max === Infinity || price <= max);
      });
    }
    
    return filtered.length;
  }, [allTrucks, searchFilters]);

  // Apply custom colours
  useCustomStyles(contentData.colours);

  // Use content data or fallback to defaults with GUARANTEED background image
  const heroContent = {
    title: contentData?.hero?.title || "MAN TRUCK SPECIALISTS - QUALITY COMMERCIAL VEHICLES",
    subtitle: contentData?.hero?.subtitle || "MAN Expertise • Bespoke Builds • Premium Quality", 
    description: contentData?.hero?.description || "Specialising in high quality MAN trucks, parts and repairs with bespoke builds and expert craftsmanship. Professional service with 30+ years experience.",
    ctaText: contentData?.hero?.ctaText || "Browse All MAN Trucks",
    ctaSecondaryText: contentData?.hero?.ctaSecondaryText || "Call Expert Team",
    // ALWAYS ensure background image is present
    backgroundImage: contentData?.hero?.backgroundImage || "https://customer-assets.emergentagent.com/job_radius-consistency/artifacts/hb14utjx_before.webp",
    darkOverlay: contentData?.hero?.darkOverlay !== undefined ? contentData?.hero?.darkOverlay : true
  };

  const contactContent = contentData.contact || {
    office_phone: "01530 231 235",
    mobile_phone: "07718 738 785",
    email: "info@lawmancommercials.com",
    address: "31 Merrylees Industrial Estate, Leeside, Desford, Leicester LE9 9FS",
    company_name: "Lawman Commercials"
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    // Remove make filter as we only do MAN trucks
    params.append('make', 'MAN');
    if (searchFilters.model) params.append('model', searchFilters.model);
    if (searchFilters.bodytype) params.append('bodytype', searchFilters.bodytype);
    if (searchFilters.priceRange) params.append('priceRange', searchFilters.priceRange);
    if (searchFilters.mileage) params.append('mileage', searchFilters.mileage);
    if (searchFilters.availability) params.append('availability', searchFilters.availability);
    
    window.location.href = `/trucks?${params.toString()}`;
  };

  const TruckCard = ({ truck, showVideo = true }) => (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group border border-gray-100">
      <div className="relative">
        <img 
          src={truck.images && truck.images.length > 0 ? truck.images[0] : "https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png"}
          alt={truck.title}
          className={`w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300 ${truck.is_sold ? 'grayscale opacity-80' : ''}`}
        />
        {truck.is_sold && <SoldRibbon size="md" />}
        {showVideo && truck.videos && truck.videos.length > 0 && (
          <div className="absolute top-3 left-3">
            <div className="bg-black bg-opacity-75 rounded-full p-2 flex items-center space-x-1 text-white text-xs">
              <Play className="h-3 w-3 fill-current" />
              <span>Video</span>
            </div>
          </div>
        )}
        {truck.available_for_hire && (
          <div className="absolute top-4 left-4">
            <div className="bg-green-500/80 backdrop-blur-sm border border-green-400/30 text-white px-3 py-1 rounded-lg text-sm font-bold shadow-xl">
              Hire Fleet
            </div>
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-lg font-bold text-black mb-2 line-clamp-2 font-inter">{truck.title}</h3>
        <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4" />
            <span>{truck.year}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Settings className="h-4 w-4" />
            <span>{truck.emissions}</span>
          </div>
        </div>
        <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
          <span>{truck.mileage ? truck.mileage.toLocaleString() : '0'} km</span>
          <span>{truck.bodytype}</span>
        </div>
        <div className="flex justify-between items-center">
          <div className="text-xl font-bold text-black">
            {truck.is_sold ? <span className="text-red-600">SOLD</span> : (truck.price ? `£${truck.price.toLocaleString()}` : 'POA')}
          </div>
          <Link 
            to={`/trucks/${truck.id}`}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded font-bold transition-colors text-sm"
          >
            View Details
          </Link>
        </div>
      </div>
    </div>
  );

  // Custom Dropdown Component with Glass-morphism
  const CustomDropdown = ({ icon: Icon, label, value, onChange, options, placeholder }) => (
    <div className="space-y-2">
      <label className="flex items-center text-sm font-bold text-white mb-2 uppercase tracking-wider">
        <Icon className="h-4 w-4 mr-2 text-orange-400" />
        {label}
      </label>
      <div className="relative">
        <select 
          value={value}
          onChange={onChange}
          className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl 
                     text-white placeholder-white/70 font-medium
                     focus:bg-white/15 focus:border-orange-400/60 focus:ring-2 focus:ring-orange-400/30 focus:outline-none
                     transition-all duration-200 appearance-none cursor-pointer
                     hover:bg-white/15 hover:border-white/30"
          style={{
            backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff80' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
            backgroundPosition: 'right 12px center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '16px'
          }}
        >
          <option value="" className="bg-gray-800 text-white">{placeholder}</option>
          {options.map(option => (
            <option key={option.value} value={option.value} className="bg-gray-800 text-white">
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <div className="bg-white">
      <SEO 
        title="Home"
        description="Professional MAN truck sales, hire & repair services in Leicester. Browse our extensive inventory of quality commercial trucks, get expert maintenance, and access genuine MAN parts. 30+ years experience."
        keywords="MAN trucks, commercial trucks Leicester, truck sales, truck hire, truck repair, MANCAT3 diagnostics, MAN parts"
        canonical="/"
      />
      
      {/* Advanced Hero Section with Search */}
      <section 
        className="relative pt-32 pb-16 -mt-24"
        style={{
          backgroundImage: `url("${heroContent.backgroundImage}")`,
          backgroundColor: '#1a1a1a',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          minHeight: '600px'
        }}
      >
        {/* Dark overlay for better text readability */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 100%)'
          }}
        ></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* LEFT COLUMN — Brand / Title / Stats */}
            <div className="lg:col-span-7 text-left">
            {contentLoading ? (
              <div className="animate-pulse">
                <div className="h-12 bg-gray-300 rounded mb-4 max-w-3xl"></div>
                <div className="h-6 bg-gray-300 rounded mb-2 max-w-2xl"></div>
                <div className="h-6 bg-gray-300 rounded max-w-4xl"></div>
              </div>
            ) : (
              <>
                {/* Main Headline */}
                <div className="mb-6">
                  <h1 className={`text-4xl lg:text-6xl xl:text-7xl font-bold leading-[0.95] uppercase tracking-tight ${
                    heroContent.backgroundImage 
                      ? 'text-white' 
                      : 'text-black'
                  }`}
                  style={{ fontFamily: '"Space Grotesk", "Helvetica Neue", Arial, sans-serif', fontWeight: 700, letterSpacing: '-0.02em' }}>
                    <span className="block">MAN Truck</span>
                    <span className={`block ${heroContent.backgroundImage ? 'text-orange-400' : 'text-orange-600'}`}>
                      Specialists
                    </span>
                  </h1>
                  <p className={`mt-5 text-xl lg:text-2xl xl:text-3xl italic font-light tracking-tight ${
                    heroContent.backgroundImage ? 'text-gray-200' : 'text-gray-700'
                  }`}
                  style={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 300, fontStyle: 'italic' }}>
                    Quality Commercial Vehicles
                  </p>
                </div>

                {/* Service badges */}
                <div className="mb-8">
                  <div className="flex flex-wrap items-center gap-3 lg:gap-4">
                    {[
                      { text: "Sales & Hire", icon: MousePointer, link: "/trucks" },
                      { text: "Bespoke Builds", icon: Settings, link: "/contact" }, 
                      { text: "Parts & Repairs", icon: Wrench, link: "/repairs" },
                      { text: "Premium Quality", icon: Star, link: "/about" }
                    ].map((badge, index) => (
                      <Link 
                        key={index}
                        to={badge.link}
                        className={`inline-flex items-center px-4 py-2 rounded-full text-sm lg:text-base font-medium transition-all duration-300 ${
                          heroContent.backgroundImage
                            ? 'bg-white/10 backdrop-blur-sm text-white border border-white/20 hover:bg-orange-500/20 hover:border-orange-400/40 hover:text-orange-300'
                            : 'bg-orange-100 text-orange-800 border border-orange-200 hover:bg-orange-200 hover:border-orange-300'
                        }`}
                        style={{ fontFamily: '"Space Grotesk", sans-serif' }}
                      >
                        <badge.icon className="mr-2 h-4 w-4" />
                        {badge.text}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <p className={`text-base lg:text-lg font-medium leading-relaxed mb-6 max-w-xl ${
                  heroContent.backgroundImage 
                    ? 'text-gray-100' 
                    : 'text-gray-700'
                }`}>
                  <span className={`font-bold ${heroContent.backgroundImage ? 'text-white' : 'text-black'}`}>Lawman</span> has over 
                  <span className={`font-bold mx-1.5 ${heroContent.backgroundImage ? 'text-orange-400' : 'text-orange-600'}`}>
                    30 years
                  </span> 
                  of industry experience and over
                  <span className={`font-bold mx-1.5 ${heroContent.backgroundImage ? 'text-orange-400' : 'text-orange-600'}`}>
                    5,000+
                  </span>
                  happy customers in MAN vehicles, parts and repairs.
                </p>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 lg:gap-4 max-w-xl">
                  {[
                    { value: '30+', label: 'Years Experience' },
                    { value: '5K+', label: 'Happy Customers' },
                    { value: 'MAN', label: 'Specialists' },
                  ].map((stat) => (
                    <div key={stat.label} className={`text-center p-4 rounded-xl ${
                      heroContent.backgroundImage 
                        ? 'bg-white/5 backdrop-blur-sm border border-white/10' 
                        : 'bg-gray-50 border border-gray-200'
                    }`}>
                      <div className={`text-2xl lg:text-3xl font-bold ${
                        heroContent.backgroundImage ? 'text-orange-400' : 'text-orange-600'
                      }`}
                      style={{ fontFamily: '"Space Grotesk", sans-serif' }}>{stat.value}</div>
                      <div className={`text-xs font-medium uppercase tracking-wider mt-1 ${
                        heroContent.backgroundImage ? 'text-gray-300' : 'text-gray-600'
                      }`}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
            </div>

            {/* RIGHT COLUMN — Search */}
            <div className="lg:col-span-5">
              <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-6 lg:p-8
                              bg-gradient-to-br from-white/15 to-white/5">
            {/* Subtle background pattern */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-orange-500/10 to-transparent pointer-events-none"></div>
            
            <div className="relative z-10">
              {/* Header */}
              <div className="mb-6 text-center">
                <div className="flex items-center justify-center mb-3">
                  <div className="bg-orange-500/20 backdrop-blur-sm rounded-full p-2.5 border border-orange-400/30">
                    <Search className="h-6 w-6 text-orange-400" />
                  </div>
                </div>
                <h2 className="text-xl lg:text-2xl font-bold text-white mb-2 tracking-tight"
                    style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                  Find Your Perfect MAN Truck
                </h2>
                <p className="text-white/70 text-sm">
                  Search our current stock for sale and hire
                </p>
                {searchResultsCount > 0 && (
                  <div className="mt-3 inline-flex items-center px-3 py-1.5 bg-green-500/20 backdrop-blur-sm 
                                  border border-green-400/30 rounded-full text-green-300 text-xs font-bold">
                    <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                    {searchResultsCount} vehicles match your criteria
                  </div>
                )}
              </div>

              {/* Search Filters — stacked single column inside narrow right pane */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <CustomDropdown 
                  icon={MousePointer}
                  label="Availability"
                  value={searchFilters.availability}
                  onChange={(e) => setSearchFilters({...searchFilters, availability: e.target.value})}
                  placeholder="Sale and Hire"
                  options={[
                    { value: "for-sale", label: "For Sale" },
                    { value: "for-hire", label: "For Hire" }
                  ]}
                />

                <CustomDropdown 
                  icon={Truck}
                  label="Vehicle Type"
                  value={searchFilters.bodytype}
                  onChange={(e) => setSearchFilters({...searchFilters, bodytype: e.target.value})}
                  placeholder="All MAN Types"
                  options={[
                    { value: "Tractor", label: "Tractor Units" },
                    { value: "Rigid", label: "Rigid Trucks" },
                    { value: "Tippers", label: "Tipper Trucks" },
                    { value: "Specialist", label: "Specialist Vehicles" }
                  ]}
                />

                <CustomDropdown 
                  icon={Gauge}
                  label="Mileage Range"
                  value={searchFilters.mileage}
                  onChange={(e) => setSearchFilters({...searchFilters, mileage: e.target.value})}
                  placeholder="Any Mileage"
                  options={[
                    { value: "50000-200000", label: "50k - 200k km" },
                    { value: "200000-400000", label: "200k - 400k km" },
                    { value: "400000-600000", label: "400k - 600k km" },
                    { value: "600000-800000", label: "600k - 800k km" },
                    { value: "800000+", label: "800k+ km" }
                  ]}
                />

                <CustomDropdown 
                  icon={PoundSterling}
                  label="Budget Range"
                  value={searchFilters.priceRange}
                  onChange={(e) => setSearchFilters({...searchFilters, priceRange: e.target.value})}
                  placeholder="Any Budget"
                  options={[
                    { value: "0-25000", label: "£0 - £25,000" },
                    { value: "25000-50000", label: "£25,000 - £50,000" },
                    { value: "50000-75000", label: "£50,000 - £75,000" },
                    { value: "75000+", label: "£75,000+" }
                  ]}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3">
                <button 
                  onClick={handleSearch}
                  className="w-full px-6 py-3.5 bg-gradient-to-r from-orange-500 to-orange-600 
                           hover:from-orange-600 hover:to-orange-700 text-white font-bold
                           rounded-xl transition-all duration-300 transform hover:shadow-xl
                           flex items-center justify-center space-x-2
                           border border-orange-400/30 backdrop-blur-sm"
                  style={{ fontFamily: '"Space Grotesk", sans-serif' }}
                >
                  <Search className="h-5 w-5" />
                  <span>Search MAN Trucks</span>
                </button>
                
                {(searchFilters.bodytype || searchFilters.mileage || searchFilters.priceRange || searchFilters.availability) && (
                  <button
                    onClick={() => setSearchFilters({ make: '', model: '', bodytype: '', priceRange: '', mileage: '', availability: '' })}
                    className="w-full px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 
                             text-white font-medium rounded-xl transition-all duration-300
                             hover:bg-white/20 hover:border-white/30"
                  >
                    Clear Filters
                  </button>
                )}
              </div>

              {/* Quick Stats */}
              <div className="mt-6 grid grid-cols-2 gap-2">
                <div className="text-center p-3 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10">
                  <div className="text-lg font-bold text-white" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>{allTrucks?.length || 0}</div>
                  <div className="text-white/70 text-xs font-medium">Total Stock</div>
                </div>
                <div className="text-center p-3 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10">
                  <div className="text-lg font-bold text-orange-400" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>{hireVehicles?.length || 0}</div>
                  <div className="text-white/70 text-xs font-medium">For Hire</div>
                </div>
              </div>
            </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vehicle Categories */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-black text-black mb-4 font-inter uppercase">
              Browse <span className="text-orange-600">MAN Trucks</span> by Category
            </h2>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/trucks?bodytype=Tractor" className="bg-white hover:bg-orange-50 border-2 border-gray-200 hover:border-orange-200 rounded px-6 py-3 text-center transition-all group">
              <div className="text-lg font-bold text-gray-700 group-hover:text-orange-600 uppercase">MAN Tractors</div>
              <div className="text-sm text-gray-500">Professional Hauling</div>
            </Link>
            <Link to="/trucks?bodytype=Rigid" className="bg-white hover:bg-orange-50 border-2 border-gray-200 hover:border-orange-200 rounded px-6 py-3 text-center transition-all group">
              <div className="text-lg font-bold text-gray-700 group-hover:text-orange-600 uppercase">MAN Rigids</div>
              <div className="text-sm text-gray-500">Versatile Solutions</div>
            </Link>
            <Link to="/trucks?bodytype=Tippers" className="bg-white hover:bg-orange-50 border-2 border-gray-200 hover:border-orange-200 rounded px-6 py-3 text-center transition-all group">
              <div className="text-lg font-bold text-gray-700 group-hover:text-orange-600 uppercase">MAN Tippers</div>
              <div className="text-sm text-gray-500">Heavy Duty Transport</div>
            </Link>
            <Link to="/trucks?bodytype=Specialist" className="bg-white hover:bg-orange-50 border-2 border-gray-200 hover:border-orange-200 rounded px-6 py-3 text-center transition-all group">
              <div className="text-lg font-bold text-gray-700 group-hover:text-orange-600 uppercase">MAN Specialist</div>
              <div className="text-sm text-gray-500">Specialised Equipment</div>
            </Link>
          </div>
        </div>
      </section>

      {/* Latest Arrivals */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-3xl font-black text-black font-inter uppercase">
                MAN Trucks <span className="text-orange-600">For Sale</span>
              </h2>
              <p className="text-gray-600">Quality MAN trucks with buy-now prices • Ready for immediate purchase</p>
            </div>
            <Link 
              to="/trucks" 
              className="text-orange-600 hover:text-orange-700 font-bold flex items-center"
            >
              View All
              <ChevronRight className="ml-1 h-5 w-5" />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {latestLoading ? (
              Array.from({ length: 8 }).map((_, index) => (
                <LoadingCard key={index} />
              ))
            ) : (
              latestTrucks.slice(0, 8).map((truck) => (
                <TruckCard key={truck.id} truck={truck} />
              ))
            )}
          </div>
        </div>
      </section>

      {/* Parts & Repairs Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-black mb-4 font-inter uppercase">
              MAN Parts & <span className="text-orange-600">Services</span>
            </h2>
            <p className="text-lg text-gray-600">
              Complete MAN truck parts supply and professional repair services
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Parts Section */}
            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-6">
                <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mr-4">
                  <Wrench className="h-8 w-8 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-black font-inter">MAN Parts Supply</h3>
                  <p className="text-gray-600">Genuine & Used Parts Available</p>
                </div>
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Genuine MAN manufacturer parts</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Quality used parts supply and sourcing</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Engine, transmission & body parts</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Next day delivery available</span>
                </div>
              </div>
              
              <Link
                to="/parts-catalogue"
                className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded font-bold transition-colors inline-flex items-center"
              >
                Browse Parts
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
            
            {/* Repairs Section */}
            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-6">
                <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mr-4">
                  <Settings className="h-8 w-8 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-black font-inter">MAN Repair Services</h3>
                  <p className="text-gray-600">MANCAT3 Diagnostic Specialists</p>
                </div>
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">MANCAT3 diagnostic capabilities</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Engine, transmission & brake repairs</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">ECU remapping & programming</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">MOT preparation & maintenance</span>
                </div>
              </div>
              
              <Link
                to="/repairs"
                className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded font-bold transition-colors inline-flex items-center"
              >
                View Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* For-Hire Vehicles */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-black mb-4 font-inter uppercase">
              MAN Vehicles <span className="text-orange-600">For Hire</span>
            </h2>
            <p className="text-lg text-gray-600">
              Professional MAN trucks available for short and long-term hire
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {hireLoading ? (
              Array.from({ length: 6 }).map((_, index) => (
                <LoadingCard key={index} />
              ))
            ) : hireError ? (
              <div className="col-span-3 text-center py-8">
                <p className="text-red-600">Unable to load hire vehicles</p>
              </div>
            ) : hireVehicles.length === 0 ? (
              <div className="col-span-3 text-center py-8 bg-white rounded-lg p-12">
                <Clock className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-700 mb-2">No Hire Vehicles Currently Available</h3>
                <p className="text-gray-600 mb-6">Our fleet is currently fully utilised. Please contact us for future availability.</p>
                <Link 
                  to="/contact"
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded font-bold transition-colors inline-flex items-center"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Enquire About Availability
                </Link>
              </div>
            ) : (
              hireVehicles.slice(0,6).map((truck) => (
                <TruckCard key={truck.id} truck={truck} />
              ))
            )}
          </div>
          
          <div className="text-center mt-12">
            <Link 
              to="/vehicles-for-hire" 
              className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded font-bold text-lg transition-colors inline-flex items-center"
            >
              View All Hire Vehicles
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Recently Sold Carousel */}
      <RecentlySoldCarousel />

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-black mb-4 font-inter uppercase">
              Why Choose <span className="text-orange-600">Our MAN Specialists?</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <Shield className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-black mb-3 font-inter">MAN Expertise</h3>
              <p className="text-gray-600 font-inter">Specialising exclusively in MAN trucks with 30+ years expertise. Every vehicle undergoes rigorous inspection with detailed service history.</p>
            </div>
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-black mb-3 font-inter">Bespoke Builds</h3>
              <p className="text-gray-600 font-inter">Custom MAN truck builds tailored to your specific requirements. Quality craftsmanship with transparent, no-hidden-fees pricing.</p>
            </div>
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <Globe className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-black mb-3 font-inter">Premium Service</h3>
              <p className="text-gray-600 font-inter">Professional delivery service across UK and export worldwide to 30+ countries. High quality vehicles, parts and repairs with expert support.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-black mb-4 font-inter uppercase">
              Customer <span className="text-orange-600">Reviews</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-2xl shadow-lg">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 font-inter">"Excellent service and quality vehicles. The team was professional and helped us find exactly what we needed."</p>
              <p className="text-orange-600 font-bold">- Sarah Johnson, Logistics Manager</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 font-inter">"Outstanding experience from start to finish. Competitive prices and reliable delivery service."</p>
              <p className="text-orange-600 font-bold">- Mike Roberts, Fleet Owner</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 font-inter">"Professional team with extensive knowledge. They made the buying process smooth and transparent."</p>
              <p className="text-orange-600 font-bold">- David Wilson, Transport Director</p>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Signup Section */}
      <section id="newsletter" className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-black text-black mb-4 font-inter uppercase">
              Stay Updated with <span className="text-orange-600">Latest Stock</span>
            </h2>
            <p className="text-lg text-gray-600 font-inter">
              Get weekly updates when new stock is added.
            </p>
          </div>
          
          <NewsletterSignup variant="inline" />
        </div>
      </section>

      {/* Repairs Section with Slanted Design */}
      <section className="py-16 bg-gray-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-center">
              {/* Left Column - Content */}
              <div className="lg:pr-16 py-12 lg:py-16 relative z-10">
                <div className="max-w-lg">
                  <h2 className="text-3xl md:text-4xl font-bold text-black mb-6 font-inter">
                    Expert MAN Truck Services
                  </h2>
                  <p className="text-lg text-gray-600 mb-6 font-inter">
                    Keep your MAN trucks running at peak performance with our comprehensive repair and maintenance services. Our MAN-certified technicians provide quality repairs to minimise downtime and maximise your fleet's efficiency.
                  </p>
                  <div className="space-y-3 mb-8">
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">24/7 Emergency MAN Repair Services</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">MAN-Certified Technicians & MANCAT3 System</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Engine, Transmission & Brake Specialists</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">MOT Preparation & Preventive Maintenance</span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link
                      to="/repairs"
                      className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded font-bold transition-colors inline-flex items-center justify-center"
                    >
                      View MAN Services
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                    <a
                      href="tel:01530231235"
                      className="border-2 border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white px-6 py-3 rounded font-bold transition-colors inline-flex items-center justify-center"
                    >
                      <Phone className="mr-2 h-5 w-5" />
                      Emergency Repairs
                    </a>
                  </div>
                </div>
              </div>

              {/* Right Column - Image with Slant */}
              <div className="relative lg:ml-8">
                {/* Image */}
                <div 
                  className="h-64 lg:h-96 bg-cover bg-center relative overflow-hidden"
                  style={{
                    backgroundImage: `url('https://customer-assets.emergentagent.com/job_radius-consistency/artifacts/9ot91r1g_CustomTruck.webp')`,
                    clipPath: 'polygon(15% 0%, 100% 0%, 100% 100%, 0% 100%)'
                  }}
                >
                  {/* Dark overlay for better contrast */}
                  <div className="absolute inset-0 bg-black/30"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 text-white" style={{ backgroundColor: '#3D3D3D' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-black mb-6 font-inter uppercase">
            Ready to Find Your Perfect MAN Truck?
          </h2>
          <p className="text-xl mb-8 font-inter text-gray-300">
            Contact our expert team for personalised service and competitive quotes
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href={`tel:${contactContent.office_phone?.replace(/\s/g, '')}`}
              className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded font-bold text-lg transition-colors inline-flex items-center justify-center"
            >
              <Phone className="mr-2 h-5 w-5" />
              Call Now: {contactContent.office_phone}
            </a>
            <Link
              to="/contact" 
              className="bg-white hover:bg-gray-100 text-black px-8 py-4 rounded font-bold text-lg transition-colors inline-flex items-center justify-center"
            >
              Request Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;